import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, Paper, Snackbar, Stack, Typography } from '@mui/material'
import MuiAlert, { AlertProps } from '@mui/material/Alert'

import { apiZodios } from '~api/apiZodios'
import TextInput from '~components/common/TextInput'
import '~components/styles/global.less'
import { BASE_URL_WITH_REACT } from '~config'
import { AppDispatch } from '~redux/store'

import { ASSET_URLS } from '../constants'
import { loginUser } from '../redux/slices/authSlice'

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant='filled' {...props} />
})

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [requireMfa, setRequireMfa] = useState(false)
  const [showLoginError, setShowLoginError] = useState(false)
  const [loginErrorMessage, setLoginErrorMessage] = useState(
    'Incorrect username or password. Please try again.',
  )
  const [isResetPassword, setIsResetPassword] = useState(false)
  const [isResetPasswordSuccess, setIsResetPasswordSuccess] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch: AppDispatch = useDispatch()

  const handleLoginUser = async (): Promise<void> => {
    try {
      const response = await apiZodios.core_userLogin({
        email,
        password,
        mfa_code: mfaCode,
      })
      if (response.require_mfa) {
        setRequireMfa(true)
        return
      }
      if (response.user_id) {
        const user = await apiZodios.core_getUser({ user_id: response.user_id })
        dispatch(loginUser(user))
        const origin = (location.state as { from?: Location })?.from?.pathname || ''
        if (origin == '') {
          window.location.href = `${BASE_URL_WITH_REACT}/cases/`
        }
        navigate(origin)
        return
      }
    } catch (_error) {
      setLoginErrorMessage('Incorrect username or password. Please try again.')
      setShowLoginError(true)
    }
  }

  const handleResetPassword = async (): Promise<void> => {
    setIsResetPasswordSuccess(false)
    try {
      const response = await apiZodios.core_resetPasswordRequest({ email: email })
      if (response) {
        setIsResetPasswordSuccess(true)
        return
      }
    } catch (_error) {}
  }

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') {
      return
    }
    setShowLoginError(false)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        padding: '20px',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: '40px',
          width: '100%',
          maxWidth: '400px',
          borderRadius: '12px',
          backgroundColor: 'white',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <img
            src={ASSET_URLS?.logo}
            alt='Polimorphic Logo'
            style={{
              width: '280px',
              marginBottom: '30px',
            }}
          />
          {!isResetPassword && (
            <Typography variant='body1' sx={{ color: 'text.secondary', marginBottom: '20px' }}>
              Enter your email and password to login.
            </Typography>
          )}
        </Box>
        <Stack spacing={2}>
          {isResetPassword && (
            <Box>
              <Typography sx={{ textAlign: 'left' }}>
                Please enter your email to reset your password.
              </Typography>
            </Box>
          )}
          <TextInput
            label='Email'
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            error={showLoginError}
            sx={{ mt: '25px' }}
          />
          {!isResetPassword && (
            <>
              <TextInput
                label='Password'
                type='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                error={showLoginError}
                sx={{ mt: '25px' }}
              />
              {requireMfa && (
                <>
                  <Typography sx={{ mt: '16px', textAlign: 'left', color: 'text.secondary' }}>
                    Please enter the verification code sent to your email.
                  </Typography>
                  <TextInput
                    label='Verification Code'
                    type='text'
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    fullWidth
                    required
                    sx={{ mt: '8px' }}
                    placeholder='Enter 6-digit code'
                  />
                </>
              )}
            </>
          )}
          <Button
            onClick={isResetPassword ? handleResetPassword : handleLoginUser}
            disabled={(isResetPassword && !email) || (!isResetPassword && (!email || !password))}
            variant='contained'
            color='secondary'
            size='large'
            fullWidth
            sx={{
              padding: '16px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            {isResetPassword
              ? isResetPasswordSuccess
                ? 'Resend Reset Email'
                : 'Reset Password'
              : 'Sign In'}
          </Button>
          {isResetPasswordSuccess && (
            <Alert severity='success'>Password reset email sent. Check your inbox.</Alert>
          )}
          <Typography
            pt='10px'
            sx={{ cursor: 'pointer', textAlign: 'center', textDecoration: 'underline' }}
            onClick={() => setIsResetPassword(!isResetPassword)}
          >
            {isResetPassword ? 'Return to login' : 'Forgot password?'}
          </Typography>
        </Stack>
      </Paper>
      <Snackbar open={showLoginError} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity='error' sx={{ width: '100%' }}>
          {loginErrorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default LoginScreen
